import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import FilePicker from "../components/FilePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const STEP_TEMPLATES = {
  convert: { op: "convert", to: "pdf" },
  watermark: { op: "watermark", text: "DRAFT", opacity: 0.3 },
  compress: { op: "compress", level: "medium" },
  protect: { op: "protect", password: "" },
  redact: { op: "redact", terms: [] },
  ocr: { op: "ocr", lang: "spa+eng" },
  summarize: { op: "summarize" },
  translate: { op: "translate", target_language: "English" },
  extract_pages: { op: "extract_pages", pages: "1" },
  reorder_pages: { op: "reorder_pages", order: "1" },
  split: { op: "split" },
};

function StepEditor({ step, onChange, onRemove, idx }) {
  const op = step.op;
  return (
    <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`step-${idx}`}>
      <div className="flex justify-between items-center mb-3">
        <span className="eyebrow">#{idx + 1} · {op}</span>
        <button className="text-xs text-red-600 hover:underline" onClick={onRemove} data-testid={`step-${idx}-remove`}>✕</button>
      </div>
      {op === "convert" && (
        <Input value={step.to || ""} onChange={(e) => onChange({ ...step, to: e.target.value })}
          placeholder="pdf, docx, html, png…"
          className="rounded-sm border-zinc-300 h-10" data-testid={`step-${idx}-to`} />
      )}
      {op === "watermark" && (
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={step.text || ""} onChange={(e) => onChange({ ...step, text: e.target.value })}
            placeholder="Text" className="rounded-sm border-zinc-300 h-10" />
          <Input type="number" step="0.05" min="0" max="1" value={step.opacity ?? 0.3}
            onChange={(e) => onChange({ ...step, opacity: parseFloat(e.target.value) })}
            placeholder="0.3" className="rounded-sm border-zinc-300 h-10" />
        </div>
      )}
      {op === "compress" && (
        <Select value={step.level || "medium"} onValueChange={(v) => onChange({ ...step, level: v })}>
          <SelectTrigger className="rounded-sm border-zinc-300 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">low</SelectItem>
            <SelectItem value="medium">medium</SelectItem>
            <SelectItem value="high">high</SelectItem>
          </SelectContent>
        </Select>
      )}
      {(op === "protect" || op === "unlock") && (
        <Input type="password" value={step.password || ""} onChange={(e) => onChange({ ...step, password: e.target.value })}
          placeholder="password" className="rounded-sm border-zinc-300 h-10" />
      )}
      {op === "redact" && (
        <textarea value={(step.terms || []).join("\n")}
          onChange={(e) => onChange({ ...step, terms: e.target.value.split(/\n+/).filter(Boolean) })}
          placeholder="one term per line"
          className="w-full rounded-sm border border-zinc-300 p-2 min-h-[80px] mono text-xs" />
      )}
      {op === "ocr" && (
        <Select value={step.lang || "spa+eng"} onValueChange={(v) => onChange({ ...step, lang: v })}>
          <SelectTrigger className="rounded-sm border-zinc-300 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="spa+eng">spa+eng</SelectItem>
            <SelectItem value="spa">spa</SelectItem>
            <SelectItem value="eng">eng</SelectItem>
          </SelectContent>
        </Select>
      )}
      {op === "translate" && (
        <Input value={step.target_language || "English"}
          onChange={(e) => onChange({ ...step, target_language: e.target.value })}
          className="rounded-sm border-zinc-300 h-10" />
      )}
      {op === "extract_pages" && (
        <Input value={step.pages || ""}
          onChange={(e) => onChange({ ...step, pages: e.target.value })}
          placeholder="1,3,5" className="rounded-sm border-zinc-300 h-10" />
      )}
      {op === "reorder_pages" && (
        <Input value={step.order || ""}
          onChange={(e) => onChange({ ...step, order: e.target.value })}
          placeholder="3,1,2" className="rounded-sm border-zinc-300 h-10" />
      )}
      {(op === "summarize" || op === "split") && (
        <span className="text-xs text-zinc-500">No parameters.</span>
      )}
    </div>
  );
}

export default function Recipes() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [steps, setSteps] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recipeName, setRecipeName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function loadRecipes() {
    try {
      const { data } = await api.get("/recipes");
      setRecipes(data);
    } catch (e) { /* ignore */ }
  }
  useEffect(() => { loadRecipes(); }, []);

  function addStep(op) {
    setSteps((s) => [...s, JSON.parse(JSON.stringify(STEP_TEMPLATES[op] || { op }))]);
  }

  async function run() {
    if (!file || steps.length === 0) {
      toast.error(t("pipeline_steps"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pipeline/run", { file_id: file.id, steps });
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function saveRecipe() {
    if (!recipeName.trim() || steps.length === 0) return;
    try {
      await api.post("/recipes", { name: recipeName, steps });
      setRecipeName("");
      toast.success(t("success"));
      await loadRecipes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function deleteRecipe(rid) {
    try {
      await api.delete(`/recipes/${rid}`);
      await loadRecipes();
    } catch {}
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12" data-testid="recipes-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_recipes")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_recipes_desc")}</p>

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 grid gap-6">
          {!file ? (
            <FilePicker accept="" onSelected={(f) => setFile(f)} />
          ) : (
            <div className="border border-zinc-200 rounded-md p-4 bg-white flex justify-between items-center">
              <div>
                <span className="eyebrow">FILE</span>
                <div className="hero-h2 text-lg mt-1">{file.original_filename}</div>
              </div>
              <Button variant="ghost" className="rounded-sm" onClick={() => { setFile(null); setResult(null); }}>
                {t("cta_clear")}
              </Button>
            </div>
          )}

          <div>
            <div className="flex justify-between items-end mb-3">
              <span className="eyebrow">{t("pipeline_steps")}</span>
              <div className="flex flex-wrap gap-2">
                {Object.keys(STEP_TEMPLATES).map((op) => (
                  <button key={op} onClick={() => addStep(op)}
                    className="text-xs uppercase tracking-wider border border-zinc-300 hover:bg-black hover:text-white rounded-sm px-3 py-1"
                    data-testid={`add-step-${op}`}>
                    + {op}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3">
              {steps.length === 0 && <div className="text-zinc-500 text-sm">— no steps —</div>}
              {steps.map((s, i) => (
                <StepEditor key={i} step={s} idx={i}
                  onChange={(next) => setSteps((arr) => arr.map((x, idx) => (idx === i ? next : x)))}
                  onRemove={() => setSteps((arr) => arr.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <Button onClick={run} disabled={busy || !file || steps.length === 0}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="pipeline-run">
              {busy ? t("processing") : `${t("pipeline_run")} →`}
            </Button>
            <div className="flex gap-2 items-end">
              <div>
                <Label className="eyebrow">{t("recipe_name")}</Label>
                <Input value={recipeName} onChange={(e) => setRecipeName(e.target.value)}
                  className="rounded-sm border-zinc-300 h-11 mt-1 w-60" data-testid="recipe-name" />
              </div>
              <Button onClick={saveRecipe} disabled={!recipeName.trim() || steps.length === 0}
                variant="outline" className="rounded-sm border-zinc-300 h-11" data-testid="recipe-save">
                💾 {t("recipe_save")}
              </Button>
            </div>
          </div>

          {result && (
            <div className="border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="pipeline-result">
              <span className="eyebrow">{t("result_ready")}</span>
              <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
              {result.log && (
                <ul className="mt-3 grid gap-1 mono text-xs text-zinc-500">
                  {result.log.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              )}
              <Button onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
                ↓ {t("cta_download")}
              </Button>
            </div>
          )}
        </div>

        <aside>
          <h2 className="hero-h2 text-2xl mb-4">{t("recipe_load")}</h2>
          <div className="grid gap-3">
            {recipes.length === 0 && <div className="text-zinc-500 text-sm">{t("recipes_empty")}</div>}
            {recipes.map((r) => (
              <div key={r.id} className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`recipe-${r.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="hero-h2 text-base">{r.name}</div>
                    <div className="text-xs text-zinc-500">{r.steps.length} steps</div>
                  </div>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => deleteRecipe(r.id)}
                    data-testid={`recipe-${r.id}-delete`}>
                    ✕
                  </button>
                </div>
                <Button variant="outline" className="rounded-sm border-zinc-300 h-9 mt-3"
                  onClick={() => setSteps(JSON.parse(JSON.stringify(r.steps)))}
                  data-testid={`recipe-${r.id}-load`}>
                  ↻ {t("recipe_load")}
                </Button>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
